import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import GalleryLightbox from "../components/GalleryLightbox";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function RygestopPage() {
  return (
    <>
      <Helmet>
        <title>Effektivt Rygestop med Akupunktur | Risskov</title>
        <meta
          name="description"
          content="Kvit tobakken med akupunktur i Risskov. Dæmp abstinenser, uro og rygetrang med professionel øreakupunktur hos RAB-godkendt behandler."
        />
        <meta property="og:title" content="Effektivt Rygestop med Akupunktur | Risskov" />
        <meta
          property="og:description"
          content="Kvit tobakken med akupunktur i Risskov. Dæmp abstinenser, uro og rygetrang med professionel øreakupunktur hos RAB-godkendt behandler."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/rygestop-behandling/" />
      </Helmet>

      {/* Page Header */}
      <section className="bg-gradient-to-b from-[#FFFAF7] via-[#FFE0CF]/30 to-[#FFFAF7] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        {/* Botanical Cherry Branch Accent from /design-elements/ (cherry-6.png) */}
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-50 pointer-events-none z-10">
          <CherryBranchImg index={6} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>NADA & ØREAKUPUNKTUR</span>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427]">
            Rygestop behandling med støtte til nikotintrang
          </h1>
          <p className="text-base sm:text-lg text-[#593427]/80 mt-3 max-w-2xl">
            Mindre fysisk uro når vanen skal brydes med kropsnåle og øre akupunktur.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 space-y-14">
        {/* Section 1: Top Split Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left / Main text */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-[#ffe0cf] border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                <SakuraFlower className="w-4 h-4" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Mindre fysisk uro når vanen skal brydes
              </h2>
            </div>
            
            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Kan du mærke, at beslutningen om at stoppe er klar, men at kroppen stadig kalder på nikotin? Rygestop behandling med{" "}
              <Link to="/akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf]">
                akupunktur
              </Link>{" "}
              og ørenåle kan støtte kroppen, når nikotintrang, abstinenser og faste rygevaner gør det svært at slippe cigaretterne.
            </p>
            
            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Hos{" "}
              <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] transition-colors">
                Kita Akupunktur
              </Link>{" "}
              i Risskov nær Lystrup, Egå og Aarhus arbejder jeg som{" "}
              <Link to="/akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b]">
                akupunktør
              </Link>{" "}
              med en kombination af kropsnåle og{" "}
              <Link to="/oere-akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b]">
                øre akupunktur
              </Link>
              . Dit cigaretforbrug, antal år som ryger og din motivation afgør, hvordan forløbet tilrettelægges, og hvor mange behandlinger der kan være relevante.
            </p>

            <div className="space-y-2.5 pt-2">
              {[
                "Ørenåle stimulerer punkter, der bruges ved nikotintrang",
                "Kropsnåle giver kroppen ro under første del af behandlingen",
                "Behandlingen kan hjælpe med at dæmpe nikotintrang og de værste abstinenser",
                "Små sterile nåle i øret bliver siddende efter behandlingen",
                "Forløbet tilpasses dit cigaretforbrug og din motivation",
              ].map((bullet, idx) => (
                <div key={idx} className="flex items-start space-x-3 bg-white/95 p-3.5 rounded-xl border border-[#ffe0cf] shadow-2xs">
                  <span className="w-5 h-5 rounded-full bg-[#d74814] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-[#593427] font-medium text-sm sm:text-base">{bullet}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: 5 Fordele Overview Card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="glass-card p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-md space-y-4 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-44 opacity-15 pointer-events-none">
                <CherryBranchImg index={1} className="w-full h-auto" />
              </div>

              <div className="relative z-10">
                <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-[#d74814] mb-1">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>Målrettet metode mod nikotin</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-serif text-[#593427]">
                  5 fordele ved rygestop behandling med ørenåle
                </h2>
              </div>

              <p className="text-[#593427]/90 leading-relaxed text-sm sm:text-base relative z-10">
                Et rygestop kræver en tydelig beslutning, men kroppen kan stadig reagere kraftigt, når nikotinen fjernes. Rygestop behandling er rettet mod den fysiske trang og de ubalancer, der kan opstå, når du vil stoppe med at ryge. Behandlingen kan støtte kroppen, mens du selv arbejder med de situationer, hvor cigaretterne normalt styrer din dag.
              </p>

              <p className="text-[#593427]/90 leading-relaxed text-sm pt-2 relative z-10">
                Støtte til et effektiv rygestop starter med en klar stopdag og en ærlig vurdering af dit forbrug. Hos Kita Akupunktur taler vi om, hvornår trangen typisk rammer, hvor meget du ryger, og hvordan behandlingen kan støtte kroppen, mens du bryder vanen.
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Sådan foregår behandlingen (3 cards) */}
        <section className="glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] space-y-6 relative overflow-hidden">
          <div className="absolute -bottom-8 -left-8 w-40 opacity-15 pointer-events-none">
            <CherryBranchImg index={4} className="w-full h-auto" />
          </div>

          <div className="relative z-10">
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Sådan foregår behandlingen
            </h2>
            <p className="text-[#593427]/80 text-sm sm:text-base mt-1">
              Et typisk rygestopforløb hos Kita Akupunktur kombinerer samtale, kropsakupunktur og permanente ørenåle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] space-y-2 shadow-2xs">
              <span className="inline-block w-8 h-8 rounded-full bg-[#ffe0cf] text-[#d74814] text-center leading-8 font-serif font-bold text-sm">
                1
              </span>
              <h3 className="font-semibold text-[#593427]">Kort samtale</h3>
              <p className="text-xs sm:text-sm text-[#593427]/80">
                Vi gennemgår dine rygevaner, motivation og udpeger de mest udfordrende tidspunkter på døgnet.
              </p>
            </div>

            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] space-y-2 shadow-2xs">
              <span className="inline-block w-8 h-8 rounded-full bg-[#ffe0cf] text-[#d74814] text-center leading-8 font-serif font-bold text-sm">
                2
              </span>
              <h3 className="font-semibold text-[#593427]">Kropsakupunktur</h3>
              <p className="text-xs sm:text-sm text-[#593427]/80">
                Afbalancerer nervesystemet og giver dyb fysisk ro på briksen under selve behandlingen.
              </p>
            </div>

            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] space-y-2 shadow-2xs">
              <span className="inline-block w-8 h-8 rounded-full bg-[#ffe0cf] text-[#d74814] text-center leading-8 font-serif font-bold text-sm">
                3
              </span>
              <h3 className="font-semibold text-[#593427]">Ørenåle / Plastre</h3>
              <p className="text-xs sm:text-sm text-[#593427]/80">
                Permanåle sættes i udvalgte zoner i øret for fortsat støtte og dæmpning af trang i hverdagen.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Relaterede behandlingsområder */}
        <section className="p-6 sm:p-8 rounded-3xl bg-[#fff5ee] border border-[#ffe0cf] space-y-4">
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
              Relaterede behandlingsområder
            </div>
            <p className="text-base font-serif text-[#593427]">
              Se også andre støttende behandlinger mod uro, vaner og stress
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Link
              to="/oere-akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Øre akupunktur & NADA →
            </Link>
            <Link
              to="/stress"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Stress & nervesystem →
            </Link>
            <Link
              to="/vaegtregulering"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Vægtregulering →
            </Link>
            <Link
              to="/japansk-akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Japansk akupunktur →
            </Link>
            <Link
              to="/akupunktur"
              className="px-3.5 py-2 rounded-xl bg-white border border-[#ffe0cf] text-xs sm:text-sm font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors shadow-2xs"
            >
              Klassisk akupunktur →
            </Link>
          </div>
          {/* Hurtige genveje */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-4 text-xs sm:text-sm">
            <Link
              to="/priser"
              className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
            >
              Se priser for rygestopbehandling
            </Link>
            <Link
              to="/om-mig"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Læs om Mayumi (Nada akupunktør)
            </Link>
            <Link
              to="/faq"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Samlet FAQ
            </Link>
            <Link
              to="/kontakt"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Bestil tid i Risskov
            </Link>
          </div>
        </section>

        {/* FAQ Section sidst på siden */}
        <section className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
          <FaqSection
            title="Ofte stillede spørgsmål om rygestopbehandling"
            subtitle="Få svar på hvordan kombinationen af krops- og øreakupunktur dæmper nikotintrang, forebygger vægtøgning og hjælper dig trygt i mål med rygestoppet."
            faqs={[
              {
                question: "Hvordan hjælper akupunktur og ørenåle med at dæmpe nikotintrangen?",
                answer: (
                  <>
                    Akupunktur stimulerer kroppens naturlige produktion af endorfiner og dopamin, som erstatter det kunstige velvære, nikotinen hidtil har fremkaldt i hjernens belønningscenter. Dette fjerner de værste fysiske abstinenser såsom hjertebanken, indre uro og irritabilitet. De små permanente{" "}
                    <Link to="/oere-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      ørenåle
                    </Link>{" "}
                    arbejder døgnet rundt i dine ørezoner for løbende at dæmpe rygetrangen i din hverdag.
                  </>
                ),
              },
              {
                question: "Hvor mange behandlinger skal der til for at blive varigt røgfri?",
                answer: (
                  <>
                    Et typisk og effektivt rygestopforløb hos Kita Akupunktur består af 2 til 3 behandlinger. Første session lægges ideelt på din første røgfrie dag for at fjerne den akutte abstinensbølge. Den anden behandling følger efter 4-7 dage for at stabilisere nervesystemet og forhindre tilbagefald. Se specifikke forløbspriser under{" "}
                    <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      priser og tilskud
                    </Link>
                    .
                  </>
                ),
              },
              {
                question: "Skal jeg være holdt op med at ryge, før jeg møder op til første behandling?",
                answer:
                  "Ja, det bedste og mest overbevisende resultat opnås, hvis du har røget din sidste cigaret aftenen før eller samme morgen. Når nikotinniveauet i kroppen dykker, og abstinenserne begynder at røre på sig, kan akupunkturen straks gribe ind og nulstille uroen. Du skal have en oprigtig personlig beslutning om at ville være røgfri for at få det maksimale ud af forløbet.",
              },
              {
                question: "Tager man på i vægt, når man stopper med at ryge med akupunktur?",
                answer: (
                  <>
                    Mange oplever normalt en vægtstigning ved rygestop, fordi de erstatter cigaretterne med søde sager for at dulme rastløsheden. I min behandling kombinerer jeg altid rygestoppunkterne med ørepunkter for appetitkontrol og sukkertrangdæmpning (se også vores forløb for{" "}
                    <Link to="/vaegtregulering" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      vægtregulering
                    </Link>
                    ). Dette holder dit blodsukker stabilt og modvirker trøstespisning.
                  </>
                ),
              },
              {
                question: "Hvad gør jeg, hvis jeg alligevel mærker en akut rygetrang derhjemme eller på arbejdet?",
                answer: (
                  <>
                    Under behandlingen får du sat mikroskopiske permanåle eller magnetkugler på ørets vigtigste zoner (
                    <Link to="/oere-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      øreakupunktur
                    </Link>
                    ). Hvis du i hverdagen oplever et pludseligt ønske om at ryge – for eksempel i sociale sammenhænge, efter kaffen eller ved stress – trykker du blot blidt på plastrene i 15-30 sekunder. Dette genaktiverer straks nerveimpulserne og får den akutte trang til at fordampe.
                  </>
                ),
              },
            ]}
          />
        </section>
      </div>
    </>
  );
}